import { useContext } from 'react'
import { useEffect, useState } from 'react'
import { Employee } from '../interfaces/employee'
import { EmployeeService } from '../services/api/employee'
import { GbsStore } from '../store'
import { Actions } from '../store/actions'
import { useBottomBar } from '../store/selectors'

export const useEmployees = (employeeService: EmployeeService) => {
  const { dispatch } = useContext(GbsStore)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false)
  const [isUsingDOB, setIsUsingDOB] = useState(false)
  const [employeesLoaded, setEmployeesLoaded] = useState(false)

  const bottomBar = useBottomBar()
  const existingEmployeesLength = bottomBar?.info?.numberOfEmployees || 1
  const avgAge = bottomBar?.info?.avgAge || 35

  const generateEmptyEmployees = (
    amount: number,
    avgAge: number
  ): Employee[] => {
    let initialId = 1
    const emptyEmp = new Array<Employee>(amount).fill({
      name: '',
      age: avgAge,
      spouse: false,
      children: 0,
      spouseAge: 0,
      id: 1
    })

    return emptyEmp.map((employee) => ({ ...employee, id: initialId++ }))
  }

  const handleEmployeeChange = (
    i: number,
    propKey: keyof Employee,
    value: string | number | boolean
  ) => {
    const newEmployees = [...employees]

    ;(newEmployees[i] as any)[propKey] = value

    setEmployees(newEmployees)
  }

  const handleAddEmployee = () => {
    const newEmployees = [...employees]
    newEmployees.push({
      name: '',
      spouse: false,
      age: bottomBar?.info?.avgAge || 35,
      dob: undefined,
      spouseDob: undefined,
      children: 0,
      id: (newEmployees[newEmployees.length - 1]?.id || 0) + 1
    })

    setEmployees(newEmployees)
    dispatch({
      type: Actions.SetInitialQuoteInformation,
      payload: { numberOfEmployees: newEmployees.length }
    })
  }

  const handleDelete = (i: number) => {
    if (employees.length === 0) return

    const newEmployees = [...employees.slice(0, i), ...employees.slice(i + 1)]

    setEmployees(newEmployees)
    dispatch({
      type: Actions.SetInitialQuoteInformation,
      payload: { numberOfEmployees: newEmployees.length }
    })
  }

  const handleDOBSwitch = (isUsingDOB: boolean) => {
    setIsUsingDOB(isUsingDOB)
  }

  useEffect(() => {
    if (employeesLoaded) return

    const createEmptyEmployees = () => {
      setEmployees(generateEmptyEmployees(existingEmployeesLength, avgAge))
      setIsLoadingEmployees(false)
    }

    setIsLoadingEmployees(true)

    employeeService
      .getEmployees()
      .then((r: { employees: Employee[]; isUsingDOB: boolean }) => {
        if (r.employees.length > 0) {
          setEmployees(r.employees)
          setIsLoadingEmployees(false)
          setIsUsingDOB(r.isUsingDOB)
        } else {
          createEmptyEmployees()
        }
      })
      .catch(() => createEmptyEmployees())
      .finally(() => setEmployeesLoaded(true))
  }, [existingEmployeesLength, avgAge, employeeService, employeesLoaded])

  return {
    employees,
    isLoadingEmployees,
    handleEmployeeChange,
    handleAddEmployee,
    handleDelete,
    isUsingDOB,
    handleDOBSwitch
  }
}
